import React, { useCallback, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import OfferPageGallery from '../OfferPageGallery'
import Pane from 'components/Common/Pane'
import PackageOptions from './FlashTourPackageOptions'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import PaneBody from 'components/Common/Pane/PaneBody'
import LayoutContainer from 'components/Common/LayoutContainer'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import ShareModal from 'components/Common/Modals/ShareModal'
import OfferIntro from './FlashTourIntro'
import OfferBNPLAd from '../OfferBNPLAd'
import { getShareModalConfig } from 'lib/customer/getShareModalConfig'
import { SummaryColumn } from '../styles'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import FlashTourSummary from './FlashTourSummary'
import { TourBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import config from 'constants/config'
import ExpiredDealsLereRecommendations from 'components/Recommendations/ExpiredDeals/ExpiredDealsLereRecommendations'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import UltraLuxUSPs from '../UltraLux/UltraLuxUSPs'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import ExportMediaContentButton from 'components/Common/ExportMediaContentButton/ExportMediaContentButton'
import { buildAgentHubQuotingURL } from 'lib/agentHub/quoting'
import SavePromptTour from 'tripPlanner/components/Bookmark/SavePrompt/SavePromptTour'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import OfferPageLabelGroup from '../OfferPageLabelGroup'

const Summary = styled(Group)`
  word-break: break-word;
`

const RelativeBlock = styled.div`
  position: relative;
`

interface Props {
  customerPortalURL: string;
  memberId: string;
  offer: App.TourOffer;
  pathname: string;
  regionCode: string;
  defaultDepositAmountPercentage: number;
  routeHistory: App.RouteHistoryState;
}

function FlashTourContent(props: Props) {
  const {
    customerPortalURL,
    memberId,
    offer,
    pathname,
    regionCode,
    defaultDepositAmountPercentage,
    routeHistory,
  } = props

  const {
    id,
    images,
    locationHeading,
    locationSubheading,
    onlinePurchaseSchedule,
    packages,
  } = offer

  const packageOptionsAnchor = useRef<HTMLDivElement>(null)

  const scrollToPackageOptions = useCallback(() => {
    packageOptionsAnchor.current?.scrollIntoView()
  }, [])

  const availableForPurchase = scheduleIsCurrent(onlinePurchaseSchedule)
  const location = [locationHeading, locationSubheading].filter(t => t).join(', ')

  const shareConfig = getShareModalConfig({
    customerPortalURL,
    image: images[0],
    memberId,
    offerId: id,
    offerLocation: location,
    pathname,
    region: regionCode,
    type: 'offer',
    utmCampaign: 'webshare',
    offerType: 'tour_v2',
  })

  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])

  const canBookMultipleRooms = config.MULTIPLE_ROOM_BOOKING_ENABLED

  const region = useAppSelector(state => state.geo.currentRegionCode)
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const isExportMediaEnabled = config.EXPORT_MEDIA_CONTENT_ENABLED
  const isAgentHubEnabled = config.agentHub.isEnabled
  const mediaExportURL = isAgentHubEnabled ? buildAgentHubQuotingURL(offer.id, { currency, region }) : undefined

  // Do not show the Expired recommendation when it comes from my escapes
  const prevPath = routeHistory.prevPath
  const shouldShowLere = !prevPath.includes('/my-escapes/order')

  return (<ProductPaletteProvider product={offer}>
    <ImmersiveSaveButtonPortal>
      <TourBookmarkButton offer={offer} />
    </ImmersiveSaveButtonPortal>
    <TripGuard>
      <SavePromptTour offer={offer} />
    </TripGuard>
    {shouldShowLere && <ExpiredDealsLereRecommendations
      hideOffers={false}
    />}
    <OfferPageGallery offer={offer} shareModalConfig={shareConfig} autoPlayVideo={!!offer.isVideoVimeoHero} />
    <RelativeBlock>
      <Pane>
        <PaneBody size="lg-y">
          <LayoutContainer>
            <VerticalSpacer gap={12}>
              <Group direction="horizontal" horizontalAlign="end" verticalAlign="center">
                <OfferPageLabelGroup offer={offer} showBadge />
                <CSSBreakpoint min="tablet">
                  <Group direction="horizontal" verticalAlign="center" gap={8} horizontalAlign="end">
                    <ShareModal shareConfig={shareConfig}>
                      <ShareWithFriendsButton />
                    </ShareModal>
                    {isExportMediaEnabled && <ExportMediaContentButton size="medium" target="_blank" href={mediaExportURL}/>}
                    <TripGuard>
                      <TourBookmarkButton offer={offer} />
                    </TripGuard>
                  </Group>
                </CSSBreakpoint>
              </Group>
              <Summary direction="horizontal" gap={16} wrap="wrap" tabletWrap="nowrap">
                <OfferIntro
                  offer={offer}
                  shareConfig={shareConfig}
                  showPackageOptions={scrollToPackageOptions}
                />
                <SummaryColumn>
                  <FlashTourSummary
                    price={offer.lowestPricePackage.price}
                    offer={offer}
                    availableForPurchase={availableForPurchase}
                    showPackageOptions={scrollToPackageOptions}
                  />
                  <OfferBNPLAd prices={allPrices} />
                </SummaryColumn>
              </Summary>
              {offer.productType === 'ultra_lux_tour' && <UltraLuxUSPs />}
            </VerticalSpacer>
          </LayoutContainer>
        </PaneBody>
      </Pane>
      <ScrollAnchor ref={packageOptionsAnchor} />
      <SoftOfferPageWalledGarden>
        {!canBookMultipleRooms && packages.length > 0 && (
          <PackageOptions
            offer={offer}
            availableForPurchase={availableForPurchase}
            packages={packages}
            defaultDepositAmountPercentage={defaultDepositAmountPercentage}
          />
        )}
        {canBookMultipleRooms && (
          'MULTIPLE ROOM BOOKING IS ENABLED'
        )}
      </SoftOfferPageWalledGarden>
    </RelativeBlock>
  </ProductPaletteProvider>
  )
}

function mapStateToProps(state: App.State) {
  return {
    regionCode: state.geo.currentRegionCode,
    pathname: state.router.location.pathname,
    customerPortalURL: state.system.customerPortalURL,
    memberId: state.auth.account.memberId,
    defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(state),
    routeHistory: state.routeHistory,
  }
}

export default connect(mapStateToProps)(FlashTourContent)
